import { parseISO } from "date-fns";
import { toZonedTime, formatInTimeZone } from "date-fns-tz";

/**
 * Parse a date value that might be a Date object or an ISO string
 * Handles both UTC strings (with 'Z' or timezone offset) and local datetime strings
 */
const parseDate = (date: Date | string): Date => {
    if (date instanceof Date) {
        return date;
    }

    const dateStr = String(date);

    // If the string has a timezone indicator (Z or +/-HH:MM), parse as ISO
    if (/[zZ]$/.test(dateStr) || /[+\-]\d{2}:\d{2}$/.test(dateStr)) {
        return parseISO(dateStr);
    }

    // If it looks like "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss" without timezone,
    // treat it as already being in the target timezone (don't convert)
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(dateStr)) {
        // For display purposes, we need to treat this as a local time in the user's timezone
        // Convert the string to a Date object but don't apply timezone conversion
        return new Date(dateStr.replace("T", " "));
    }

    // Fallback: try to parse as ISO
    return parseISO(dateStr);
};

/**
 * Convert a date to a specific timezone
 * @param date - Date to convert (can be Date object or ISO string from API)
 * @param timeZoneValue - IANA timezone string (e.g., 'America/New_York')
 * @returns Date object in the specified timezone
 */
export const convertToTimezone = (
    date: Date | string,
    timeZoneValue: string
): Date => {
    const dateObj = parseDate(date);
    return toZonedTime(dateObj, timeZoneValue);
};

/**
 * Format a date in a specific timezone
 * @param date - Date to format (can be Date object or ISO string from API)
 * @param timeZoneValue - IANA timezone string (e.g., 'America/New_York')
 * @param formatStr - Format string (default: 'MM/dd/yyyy hh:mm a')
 * @returns Formatted date string in the specified timezone
 */
export const formatDateInTimezone = (
    date: Date | string,
    timeZoneValue: string,
    formatStr: string = "MM/dd/yyyy hh:mm a"
): string => {
    const dateObj = parseDate(date);
    return formatInTimeZone(dateObj, timeZoneValue, formatStr);
};

/**
 * Format a date with timezone abbreviation
 * @param date - Date to format (can be Date object or ISO string from API)
 * @param timeZoneValue - IANA timezone string (e.g., 'America/New_York')
 * @param abbrevName - Timezone abbreviation (e.g., 'EST')
 * @param formatStr - Format string (default: 'MM/dd/yyyy hh:mm a')
 * @returns Formatted date string with timezone abbreviation
 */
export const formatDateWithTimezoneAbbrev = (
    date: Date | string,
    timeZoneValue: string,
    abbrevName?: string | null,
    formatStr: string = "MM/dd/yyyy hh:mm a"
): string => {
    const formattedDate = formatDateInTimezone(date, timeZoneValue, formatStr);
    return abbrevName ? `${formattedDate} ${abbrevName}` : formattedDate;
};

/**
 * Get current date/time in a specific timezone
 * @param timeZoneValue - IANA timezone string (e.g., 'America/New_York')
 * @returns Current date/time in the specified timezone
 */
export const getCurrentDateInTimezone = (timeZoneValue: string): Date => {
    return toZonedTime(new Date(), timeZoneValue);
};

/**
 * Format date for display with user's timezone from context
 * This is a helper that should be used with the usePreference hook
 * @param date - Date to format (can be Date object or ISO string from API)
 * @param timeZoneValue - IANA timezone string from user preferences
 * @param abbrevName - Timezone abbreviation from user preferences
 * @returns Formatted date string
 */
export const formatDateForUser = (
    date: Date | string | null | undefined,
    timeZoneValue?: string | null,
    abbrevName?: string | null
): string => {
    if (!date) return "";

    // Default to America/New_York (EST) if no timezone preference
    const tz = timeZoneValue || "America/New_York";
    const abbrev = abbrevName || "EST";

    return formatDateWithTimezoneAbbrev(date, tz, abbrev, "MM/dd/yyyy hh:mm a");
};

/**
 * Get timezone display name with abbreviation
 * @param timeZoneName - Full timezone name (e.g., "Eastern Standard Time")
 * @param abbrevName - Timezone abbreviation (e.g., "EST")
 * @returns Combined display string (e.g., "Eastern Standard Time (EST)")
 */
export const getTimezoneDisplayName = (
    timeZoneName?: string | null,
    abbrevName?: string | null
): string => {
    if (!timeZoneName) return "";
    return abbrevName ? `${timeZoneName} (${abbrevName})` : timeZoneName;
};

/**
 * Hook-friendly utility to format date with timezone from preference context
 * Usage: const formatted = useFormattedDate(myDate);
 */
export const createDateFormatter = (
    timeZoneValue?: string | null,
    abbrevName?: string | null
) => {
    return (date: Date | string | null | undefined, formatStr?: string) => {
        if (!date) return "";

        // Default to America/New_York (EST) if no timezone preference
        const tz = timeZoneValue || "America/New_York";
        const abbrev = abbrevName || "EST";

        return formatDateWithTimezoneAbbrev(
            date,
            tz,
            abbrev,
            formatStr || "MM/dd/yyyy hh:mm a"
        );
    };
};
